import {
  LDEvaluationDetail,
  withLDProvider
} from "launchdarkly-react-client-sdk";
import React from "react";
import ReactDOM from "react-dom/client";

import { enableMapSet } from "immer";

import "components/word-processor-field/word-processor.css";
import { watchFailedAssetRequests } from "utils/asset-cache-repair.util";
import { reportDomObserverAnomalies } from "utils/dom-observer-guard.util";
import { pruneOrphanedLaunchDarklyCache } from "utils/launchdarkly-storage.util";
import {
  handleVitePreloadError,
  pruneStaleChunkReloadMarkers
} from "utils/lazy-with-retry.util";
import { viteEnv } from "utils/vite-env.util";

import App from "./App";
import "./app/chart-js";
import "./index.css";
import "./polyfills";
import reportWebVitals from "./reportWebVitals";
import setupDatadog from "./setup/datadog";
import { recordFlagEvaluation } from "./setup/datadog-flag-evaluations";
import { createLaunchDarklyClient } from "./setup/launchdarkly-client";
import setupMarkerIO from "./setup/marker-io";
import "./setup/mui";

import "animate.css";

window.global ||= window;

enableMapSet();

// ENG-1813: must run before the LaunchDarkly client is created below, which
// reads the bootstrap cache. Clears the cache only when it has grown past what a
// healthy profile can account for — an already-full browser recovers here
// instead of the user clearing site data.
pruneOrphanedLaunchDarklyCache();

const clientSideID = viteEnv.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID!;

// SN-20181: the client is created here and handed to the provider once it has
// settled, rather than letting `withLDProvider` create it — see
// setup/launchdarkly-client.ts for why the provider's own path can leave the
// app's flags nearly empty for a whole page load.
const ldClient = createLaunchDarklyClient(
  clientSideID,
  {
    kind: "user",
    key: "anonymous",
    anonymous: true
  },
  {
    // Caches the last-known flag set in localStorage so a repeat visit can
    // render immediately from cache while the SDK confirms/updates values in
    // the background, instead of the app waiting on a fresh network round
    // trip on every load (ENG-1729). Doesn't change the first-ever-visit
    // blocking wait — that's a separate, larger follow-up.
    bootstrap: "localStorage",
    inspectors: [
      {
        type: "flag-used",
        name: "dd-inspector",
        method: (key: string, detail: LDEvaluationDetail) => {
          recordFlagEvaluation(key, detail.value);
        }
      }
    ]
  }
);

const LDProvider = withLDProvider({ clientSideID, ldClient })(App);

// Guarded one-shot reload on failed chunk preloads (ENG-1479) — the previous
// unconditional reload looped forever on persistent local fetch failures
// (DEV-2147). One auto-reload per app version per tab; a new deploy gets a
// fresh budget (stale-version markers pruned here).
pruneStaleChunkReloadMarkers();
window.addEventListener("vite:preloadError", handleVitePreloadError);
// Records failed /assets/* requests so a recovery reload can overwrite their
// cached 404s first (DEV-3172). Before render, so boot-time failures count.
watchFailedAssetRequests();

window.addEventListener("error", (e) => {
  if (e.message?.includes("ResizeObserver loop")) e.stopImmediatePropagation();
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(<LDProvider />);

// Defer non-critical SDK initialization to reduce TBT.
// Datadog SDK v6 buffers API calls (addError, setUser, etc.) made before
// .init() and replays them once init completes — but the buffer holds only
// 500 calls and drops the oldest, so nothing high-volume may feed it
// (ENG-2067: flag reads evicted setUser; see setup/datadog-flag-evaluations).
const idleInit =
  typeof requestIdleCallback === "function"
    ? (cb: () => void) => requestIdleCallback(cb, { timeout: 3000 })
    : (cb: () => void) => setTimeout(cb, 1);
idleInit(() => {
  setupDatadog();
  // DEV-3013: the observer guard runs in ./polyfills, long before Datadog is
  // configured — this is what gets its findings off the browser. Kept next to
  // the Datadog init so a failure in another SDK's setup cannot skip it.
  reportDomObserverAnomalies();
  setupMarkerIO();
});
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
